import { getDb } from '../../core/db';
import { newId } from '../../core/ids';
import type { Moment } from '../../core/types';
import { getMomentAccountId } from './accountScope';

export interface MomentComment { id: string; content: string; createdAt: number }
/** Private browser data. Never included in Moments cloud synchronization. */
export interface MomentInteraction {
  id: string;
  userId: string;
  postId: string;
  presetId: string;
  liked: boolean;
  comments: MomentComment[];
  updatedAt: number;
}
export interface MomentInteractionContext { postId: string; liked: boolean; comments: string[]; content?: string }

function interactionId(userId: string, postId: string): string {
  return JSON.stringify([userId, postId]);
}
function accessible(moment: Moment | undefined, userId: string): moment is Moment {
  return moment !== undefined && (moment.userId === undefined || moment.userId === userId);
}
function requireAccount(): string {
  const userId = getMomentAccountId();
  if (!userId) throw new Error('请先登录后再点赞或评论');
  return userId;
}

export async function loadMomentInteraction(postId: string): Promise<MomentInteraction | null> {
  const userId = getMomentAccountId();
  if (!userId) return null;
  const db = getDb();
  const [post, record] = await Promise.all([db.moments.get(postId), db.momentInteractions.get(interactionId(userId, postId))]);
  return getMomentAccountId() === userId && accessible(post, userId) ? record ?? null : null;
}

async function updateInteraction(postId: string, update: (record: MomentInteraction) => void): Promise<void> {
  const userId = requireAccount();
  const db = getDb();
  await db.transaction('rw', db.moments, db.momentInteractions, async () => {
    const post = await db.moments.get(postId);
    if (!accessible(post, userId)) throw new Error('这条朋友圈已不可用');
    const id = interactionId(userId, postId);
    const record = await db.momentInteractions.get(id) ?? {
      id, userId, postId, presetId: post.presetId, liked: false, comments: [], updatedAt: Date.now(),
    };
    update(record);
    record.updatedAt = Date.now();
    if (getMomentAccountId() !== userId) throw new Error('登录账户已变化，请重试');
    if (!record.liked && record.comments.length === 0) await db.momentInteractions.delete(id);
    else await db.momentInteractions.put(record);
  });
}

export async function toggleMomentLike(postId: string): Promise<void> {
  await updateInteraction(postId, record => { record.liked = !record.liked; });
}
export async function addMomentComment(postId: string, text: string): Promise<void> {
  const content = text.trim();
  if (!content) throw new Error('请输入评论内容');
  if (content.length > 500) throw new Error('评论不能超过 500 字');
  await updateInteraction(postId, record => {
    record.comments.push({ id: newId(), content, createdAt: Date.now() });
  });
}
export async function deleteMomentComment(postId: string, commentId: string): Promise<void> {
  await updateInteraction(postId, record => { record.comments = record.comments.filter(comment => comment.id !== commentId); });
}

/** Bounded, account-scoped request context; callers must only send it while intimacy is enabled. */
export async function getMomentInteractionContext(presetId: string): Promise<MomentInteractionContext[]> {
  const userId = getMomentAccountId();
  if (!userId) return [];
  const db = getDb();
  const rows = await db.momentInteractions.where('[userId+presetId]').equals([userId, presetId]).toArray();
  const recent = rows.sort((a, b) => b.updatedAt - a.updatedAt).slice(0, 20);
  const posts = await db.moments.bulkGet(recent.map(row => row.postId));
  if (getMomentAccountId() !== userId) return [];
  return recent.flatMap((row, index) => {
    const post = posts[index];
    if (!accessible(post, userId) || post.presetId !== presetId) return [];
    return [{ postId: post.id, liked: row.liked, comments: row.comments.slice(-5).map(comment => comment.content.slice(0, 500)),
      ...(post.content ? { content: post.content.slice(0, 1000) } : {}) }];
  });
}
